import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { useAuthStore } from '@/store/auth'
import { functionError } from '@/hooks/useMix'

/**
 * Chatting with Emma.
 *
 * A message goes to the emma-chat function, which carries it to Emma with the
 * account as context and hands back her reply. Five typed questions a week are
 * included; after that each one is 1 seed (from Emma's silo if it holds any,
 * otherwise this week's seeds, then gifts, then purchased).
 *
 * Every send carries its own id, so a retry after a dropped connection returns
 * the stored reply rather than asking (and charging) twice.
 */

export interface EmmaMessage {
  id: string
  role: 'user' | 'emma'
  body: string
  paid_with?: 'free' | 'silo' | 'seeds' | null
  created_at: string
  /** Local only: a message waiting on Emma, or one that failed. */
  pending?: boolean
  failed?: string
  client_message_id?: string | null
}

export interface EmmaConversation {
  id: string
  title: string | null
  updated_at: string
}

export function useConversations() {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['emma-conversations', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emma_conversations').select('id, title, updated_at')
        .eq('user_id', userId!).order('updated_at', { ascending: false }).limit(20)
      if (error) throw error
      return data as EmmaConversation[]
    },
  })
}

export function useConversationMessages(conversationId: string | null) {
  return useQuery({
    queryKey: ['emma-messages', conversationId],
    enabled: !!conversationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emma_messages').select('id, role, body, paid_with, created_at, client_message_id')
        .eq('conversation_id', conversationId!).order('created_at', { ascending: true })
      if (error) throw error
      return data as EmmaMessage[]
    },
  })
}

export class OutOfGrainError extends Error {
  constructor() { super('Emma is out of grain'); this.name = 'OutOfGrainError' }
}

export interface EmmaReply {
  conversation_id: string
  reply: string
  paid_with: 'free' | 'silo' | 'seeds'
  free_remaining: number
  silo_remaining: number
}

export function useSendToEmma() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      message: string
      conversationId: string | null
      clientMessageId: string
      storyId?: string | null
      /** The screen the person is on (Brian: Emma asks about the screen you're on). */
      screen?: { key: string; name: string; path: string } | null
    }) => {
      const { data, error } = await supabase.functions.invoke('emma-chat', {
        body: {
          message: args.message,
          conversation_id: args.conversationId,
          client_message_id: args.clientMessageId,
          story_id: args.storyId ?? null,
          screen: args.screen ?? null,
        },
      })
      if (error) {
        const e = await functionError(error)
        if (e.status === 402 || /out of grain/i.test(e.message)) throw new OutOfGrainError()
        throw e
      }
      return data as EmmaReply
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['seed-balance', clientId] })
      qc.invalidateQueries({ queryKey: ['emma-conversations'] })
      qc.invalidateQueries({ queryKey: ['emma-messages', data.conversation_id] })
      qc.invalidateQueries({ queryKey: ['coin-case'] })
    },
  })
}
